// Segni rossi sullo screenshot della segnalazione (FeedbackModal): l'utente clicca
// sull'immagine per indicare il punto esatto; i cerchi vengono disegnati nell'immagine
// prima del caricamento, cosi' chi legge la segnalazione li vede in qualsiasi strumento.

/** Punto in coordinate relative all'immagine (0..1). */
export interface ScreenshotMark {
  x: number;
  y: number;
}

export const MARK_COLOR = '#EB0000';
export const MAX_MARKS = 10;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** Converte un clic (coordinate client) nel punto relativo all'immagine mostrata. */
export function relativePoint(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
): ScreenshotMark | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    x: clamp01((clientX - rect.left) / rect.width),
    y: clamp01((clientY - rect.top) / rect.height),
  };
}

/** Raggio del cerchio in pixel dell'immagine: proporzionato, mai troppo piccolo. */
export function markRadius(width: number, height: number): number {
  return Math.max(14, Math.round(Math.min(width, height) * 0.04));
}

/** Spessore del tratto in pixel dell'immagine. */
export function markLineWidth(width: number, height: number): number {
  return Math.max(3, Math.round(markRadius(width, height) / 5));
}

/** Aggiunge un segno rispettando il massimo. */
export function addMark(marks: ScreenshotMark[], mark: ScreenshotMark): ScreenshotMark[] {
  if (marks.length >= MAX_MARKS) return marks;
  return [...marks, mark];
}

type MarkCtx = Pick<
  CanvasRenderingContext2D,
  'beginPath' | 'arc' | 'stroke' | 'fill' | 'moveTo' | 'lineTo'
> & { strokeStyle: unknown; fillStyle: unknown; lineWidth: number };

/** Disegna i cerchi (con un pallino centrale) sul contesto di un canvas grande w x h. */
export function drawMarks(ctx: MarkCtx, width: number, height: number, marks: ScreenshotMark[]): void {
  const r = markRadius(width, height);
  ctx.strokeStyle = MARK_COLOR;
  ctx.fillStyle = MARK_COLOR;
  ctx.lineWidth = markLineWidth(width, height);
  for (const m of marks) {
    const cx = m.x * width;
    const cy = m.y * height;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(2, Math.round(r / 6)), 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Restituisce un nuovo PNG con i segni disegnati. Senza segni restituisce il blob originale.
 * In caso di errore (browser senza canvas/createImageBitmap) restituisce l'originale:
 * la segnalazione deve partire comunque.
 */
export async function annotateScreenshot(blob: Blob, marks: ScreenshotMark[]): Promise<Blob> {
  if (marks.length === 0) return blob;
  try {
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.drawImage(bitmap, 0, 0);
    drawMarks(ctx, bitmap.width, bitmap.height, marks);
    bitmap.close?.();
    const out = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    return out ?? blob;
  } catch (err) {
    console.warn('annotateScreenshot: segni non applicati', err);
    return blob;
  }
}
